import React from 'react';
import { Wind, Flame, Zap, AirVent, ArrowRight, CheckCircle2, Phone, Droplet, Thermometer, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { services } from '../data/mockData';
import Header from './Header';
import Footer from './Footer';

const iconMap = {
  wind: Wind,
  flame: Flame,
  zap: Zap,
  airVent: AirVent,
  droplet: Droplet,
  thermometer: Thermometer
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-teal-50 z-0"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                  Experts en solutions énergétiques
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Votre Confort,
                <span className="block bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                  Notre Priorité
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Solutions complètes en climatisation, chauffage, pompe à chaleur et ventilation. 
                Des installations certifiées pour un confort optimal toute l'année dans les Hauts-de-France.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => window.location.href = 'tel:0783518918'}
                  size="lg" 
                  className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-8 h-14 text-lg"
                >
                  <Phone className="mr-2" size={20} />
                  07 83 51 89 18
                </Button>
                <Button 
                  onClick={() => window.location.href = 'mailto:cg.enrgies@gmail.com'}
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 h-14 text-lg"
                >
                  Nous Contacter
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="climacg.png"
                  alt="Technicien CG Énergies"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Nos Services</span>
            <h2 className="text-4xl font-bold mt-4 mb-4">Des Solutions Complètes</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez notre gamme complète de services pour votre confort thermique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-0 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                        <IconComponent className="text-blue-600" size={24} />
                      </div>
                      <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle2 className="text-green-500 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={() => window.location.href = 'tel:0783518918'}
                      className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Nous Contacter
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Contact</span>
              <h2 className="text-4xl font-bold mt-4 mb-4">Contactez-Nous</h2>
              <p className="text-xl text-gray-600">
                N'hésitez pas à nous contacter pour tous vos besoins en solutions énergétiques
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Téléphone */}
              <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Phone className="text-blue-600" size={32} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Téléphone</h3>
                  <a href="tel:0783518918" className="text-blue-600 hover:text-blue-700 font-medium">
                    07 83 51 89 18
                  </a>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Mail className="text-blue-600" size={32} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Email</h3>
                  <a href="mailto:cg.enrgies@gmail.com" className="text-blue-600 hover:text-blue-700 font-medium break-all">
                    cg.enrgies@gmail.com
                  </a>
                </CardContent>
              </Card>

              {/* Localisation */}
              <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="text-blue-600" size={32} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Localisation</h3>
                  <p className="text-gray-600 font-medium">
                    Béthune<br />
                    Hauts-de-France
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Horaires */}
            <Card className="mt-8 border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-xl mb-3 text-gray-900">Horaires d'Ouverture</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                      <div className="flex justify-between">
                        <span className="font-medium">Lundi - Samedi:</span>
                        <span>8h - 19h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Dimanche:</span>
                        <span className="text-orange-600 font-medium">Sur urgence</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = 'tel:0783518918'}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-8 h-14 text-lg"
              >
                <Phone className="mr-2" size={20} />
                Appelez-nous maintenant
              </Button>
              <Button 
                onClick={() => window.location.href = 'mailto:cg.enrajes@gmail.com'}
                size="lg"
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 h-14 text-lg"
              >
                <Mail className="mr-2" size={20} />
                Envoyez un email
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;